import React, { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { housestylesApi, styleExamplesApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { PageHeader } from '@/components/layout'
import { toast } from 'sonner'
import { Save, FileText, Languages, Megaphone, Clapperboard, Plus, Trash2, Sparkles, Loader2 } from 'lucide-react'

export const Route = createFileRoute('/admin/style')({
  component: HouseStyleManager,
})

const CONTENT_TYPES = [
  { id: 'general', name: '媒体稿件', icon: FileText, description: '适用于新闻稿、公关稿、媒体发布' },
  { id: 'subtitle', name: '视频字幕', icon: Languages, description: '适用于视频听翻、字幕翻译' },
  { id: 'marketing', name: '市场文案', icon: Megaphone, description: '适用于广告、推文、宣传册' },
  { id: 'drama', name: '短剧剧本', icon: Clapperboard, description: '适用于短剧剧本中译英，台词口语化、美式本地化' },
]

/** 全局风格库管理页：按内容类型维护 Markdown 风格指南并提供预览。 */
function HouseStyleManager() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('marketing')
  const [markdown, setMarkdown] = useState('')

  const { data: stylesResponse, isLoading } = useQuery({
    queryKey: ['house-styles'],
    queryFn: () => housestylesApi.getList(),
  })

  const saveMutation = useMutation({
    mutationFn: (data: { contentType: string; markdownContent: string }) => 
      housestylesApi.upsert(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['house-styles'] })
      toast.success('保存成功')
    },
    onError: (error: any) => {
      toast.error(`保存失败: ${error.message || '未知错误'}`)
    }
  })

  const styles = (stylesResponse as any)?.data || []
  
  // 当切换标签或数据加载完成时，同步当前编辑器的内容
  React.useEffect(() => {
    const currentStyle = styles.find((s: any) => s.contentType === activeTab)
    setMarkdown(currentStyle?.markdownContent || '')
  }, [activeTab, styles])

  /** 保存当前 tab 的风格指南内容。 */
  const handleSave = () => {
    console.debug('[HouseStyle] 保存风格：', { contentType: activeTab, length: markdown.length })
    saveMutation.mutate({
      contentType: activeTab,
      markdownContent: markdown,
    })
  }

  if (isLoading) {
    return <div className="py-8 text-center text-sm text-muted-foreground">正在加载风格定义...</div>
  }

  return (
    <div className="space-y-8">
      <PageHeader title="全局风格库" description="定义不同类型内容的 AI 翻译风格和写作准则" />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 w-full h-auto p-1 bg-muted">
          {CONTENT_TYPES.map((type) => (
            <TabsTrigger 
              key={type.id} 
              value={type.id}
              className="flex flex-col gap-1 py-3 data-[state=active]:bg-background text-xs sm:text-sm"
            >
              <type.icon className="h-4 w-4" />
              <span>{type.name}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {CONTENT_TYPES.map((type) => (
          <TabsContent key={type.id} value={type.id} className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{type.name} 风格指南</CardTitle>
                    <CardDescription>{type.description}</CardDescription>
                  </div>
                  <Button onClick={handleSave} disabled={saveMutation.isPending}>
                    <Save className="mr-2 h-4 w-4" />
                    {saveMutation.isPending ? '保存中...' : '保存修改'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Markdown 编辑器</label>
                    <Textarea
                      placeholder="输入风格指南，例如：\n- 使用第二人称\n- 语气亲切友好\n- 术语保持一致..."
                      className="min-h-[500px] font-mono text-sm leading-relaxed"
                      value={markdown}
                      onChange={(e) => setMarkdown(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">预览</label>
                    <div className="min-h-[500px] p-4 rounded-md border bg-slate-50 overflow-auto prose prose-sm max-w-none">
                      {markdown ? (
                        <div className="whitespace-pre-wrap">{markdown}</div>
                      ) : (
                        <p className="text-muted-foreground italic text-center mt-20">
                          暂无内容，请在左侧输入 Markdown 格式的风格指南
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 范本库：示例译文对 + 从范本提炼风格指南 */}
            <StyleExamplesPanel
              contentType={type.id}
              contentTypeName={type.name}
              onExtracted={(guide) => setMarkdown(guide)}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}

/**
 * 风格范本库面板：按内容类型维护示例译文对，并可一键从范本提炼风格指南。
 *
 * @param contentType 内容类型标识（general/subtitle/marketing）
 * @param contentTypeName 内容类型展示名（用于 LLM 提炼上下文）
 * @param onExtracted 提炼到风格指南后回调，将结果填入父级风格指南编辑器
 */
function StyleExamplesPanel({
  contentType,
  contentTypeName,
  onExtracted,
}: {
  contentType: string
  contentTypeName: string
  onExtracted: (styleGuide: string) => void
}) {
  const queryClient = useQueryClient()
  const [newSource, setNewSource] = useState('')
  const [newTarget, setNewTarget] = useState('')
  const [newNotes, setNewNotes] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['style-examples', contentType],
    queryFn: () => styleExamplesApi.getList(contentType),
  })
  const examples = (data as any)?.data || []

  const addMutation = useMutation({
    mutationFn: () =>
      styleExamplesApi.add(contentType, {
        sourceText: newSource,
        targetText: newTarget,
        notes: newNotes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['style-examples', contentType] })
      setNewSource('')
      setNewTarget('')
      setNewNotes('')
      toast.success('范本已添加')
    },
    onError: (error: any) => toast.error(error?.error || '添加失败'),
  })

  const removeMutation = useMutation({
    mutationFn: (id: string) => styleExamplesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['style-examples', contentType] })
      toast.success('范本已删除')
    },
    onError: (error: any) => toast.error(error?.error || '删除失败'),
  })

  const extractMutation = useMutation({
    mutationFn: () =>
      styleExamplesApi.extract({
        contentType,
        contentTypeName,
        examples: examples.map((ex: any) => ({
          sourceText: ex.sourceText,
          targetText: ex.targetText,
          notes: ex.notes,
        })),
      }),
    onSuccess: (resp: any) => {
      const styleGuide = resp?.data?.styleGuide
      if (styleGuide) {
        onExtracted(styleGuide)
        toast.success('已生成风格指南，请在左侧编辑器中查看并保存')
      }
    },
    onError: (error: any) => toast.error(error?.error || '提炼失败'),
  })

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" /> 范本库
            </CardTitle>
            <CardDescription>
              维护「{contentTypeName}」的示例译文对（体现目标风格），作为提炼风格指南的原料
            </CardDescription>
          </div>
          <Button
            onClick={() => extractMutation.mutate()}
            disabled={extractMutation.isPending || examples.length === 0}
          >
            {extractMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            {extractMutation.isPending ? '提炼中...' : '从范本生成风格指南'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">加载范本中...</p>
        ) : examples.length === 0 ? (
          <p className="text-sm text-muted-foreground">暂无范本，请在下方添加示例译文对。</p>
        ) : (
          <div className="space-y-3">
            {examples.map((ex: any) => (
              <div key={ex.id} className="p-3 rounded-md border bg-slate-50/50">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-1 flex-1">
                    <p className="text-xs text-muted-foreground">原文</p>
                    <p className="text-sm whitespace-pre-wrap">{ex.sourceText}</p>
                    <p className="text-xs text-muted-foreground mt-2">译文</p>
                    <p className="text-sm whitespace-pre-wrap">{ex.targetText}</p>
                    {ex.notes && (
                      <>
                        <p className="text-xs text-muted-foreground mt-2">要点</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{ex.notes}</p>
                      </>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive shrink-0"
                    onClick={() => removeMutation.mutate(ex.id)}
                    disabled={removeMutation.isPending}
                    aria-label="删除范本"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 新增范本表单 */}
        <div className="p-3 rounded-md border border-dashed space-y-2">
          <p className="text-sm font-medium">新增范本</p>
          <Input
            placeholder="原文"
            value={newSource}
            onChange={(e) => setNewSource(e.target.value)}
          />
          <Input
            placeholder="译文（体现目标风格）"
            value={newTarget}
            onChange={(e) => setNewTarget(e.target.value)}
          />
          <Input
            placeholder="要点说明（可选）：为何这样译 / 应遵循的风格要点"
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
          />
          <Button
            size="sm"
            onClick={() => addMutation.mutate()}
            disabled={addMutation.isPending || !newSource || !newTarget}
          >
            <Plus className="mr-1 h-4 w-4" />
            {addMutation.isPending ? '添加中...' : '添加范本'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
